<script setup>
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import InputField from '@js/components/fields/InputField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastSuccess } from '@js/utils/toast';
import { computed, reactive, ref } from 'vue';
import { number, object } from 'yup';

const props = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    periodType: {
        type: String,
        default: 'month',
    },
    periods: {
        type: Number,
        required: true,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const isQuarterly = computed(() => props.periodType === 'quarter');

const title = computed(() =>
    isQuarterly.value
        ? `Trimestres de rapports visibles — ${props.tenant.name}`
        : `Mois de rapports visibles — ${props.tenant.name}`,
);

const label = computed(() =>
    isQuarterly.value
        ? 'Nombre de trimestres de rapports visibles par le client'
        : 'Nombre de mois de rapports visibles par le client',
);

const legend = computed(() =>
    isQuarterly.value
        ? 'Nombre de derniers trimestres de rapport accessibles au client (défaut : 4). 0 masque complètement l’onglet trimestriel.'
        : 'Nombre de derniers mois de rapport accessibles au client (défaut : 2). 0 masque complètement l’onglet mensuel.',
);

const isSubmitting = ref(false);

const form = reactive({
    numberOfVisiblePeriods: props.periods,
});

const validationSchema = object({
    numberOfVisiblePeriods: number()
        .typeError('Le nombre de périodes est requis')
        .required('Le nombre de périodes est requis')
        .min(0, 'Le nombre de périodes ne peut pas être négatif'),
});

const isOpen = ref(false);

function setIsOpen(value) {
    isOpen.value = value;
}

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.customer.postUpdateReportVisiblePeriods',
                {
                    tenant: props.tenant.id,
                },
            ),
            {
                periodType: props.periodType,
                numberOfVisiblePeriods: Number(form.numberOfVisiblePeriods),
            },
        )
        .then(() => {
            toastSuccess(
                isQuarterly.value
                    ? 'Nombre de trimestres de rapports visibles mis à jour.'
                    : 'Nombre de mois de rapports visibles mis à jour.',
            );

            setIsOpen(false);

            if (props.callback) {
                props.callback();
            }
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            console.error(error);
        });
}
</script>

<template>
    <DialogDrawer v-model="isOpen">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title>
            {{ title }}
        </template>

        <template #full-content>
            <FormRoot
                :validation-schema="validationSchema"
                :submit="submit"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <FormContent>
                        <InputField
                            v-model="form.numberOfVisiblePeriods"
                            field-id="numberOfVisiblePeriods"
                            field-name="numberOfVisiblePeriods"
                            :label="label"
                            :legend="legend"
                            type="number"
                            min="0"
                            step="1"
                        />
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    withoutBackButton
                />
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
